// biome-ignore-all lint/style/useImportType: ChangeDetectorRef, Store, DrawerNotificationService, MatDialog used at runtime in constructor; Component, ViewChild, ViewChildren, Input, Output, EventEmitter used at runtime as decorators
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  type OnChanges,
  type OnDestroy,
  type OnInit,
  Optional,
  Output,
  type QueryList,
  type SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { type DeviceAsset, ExecutionStatus, isEligibilityAction } from '@cdw-ae/common-data';
import {
  type CdwBulkFieldUpdateComponent,
  type DeviceFieldValues,
  FieldTemplate,
  type FieldUpdate,
  type FieldUpdateConfig,
  type ValidationStateChangeEvent,
} from '@cdw-ae/common-ui-components-bulk-field-update';
import {
  CdwConfirmationDialogComponent,
  type CdwConfirmationDialogData,
} from '@cdw-ae/common-ui-components-dialog';
import {
  type CdwDrawersState,
  closeDrawer,
  DrawerNotificationService,
  openDrawer,
} from '@cdw-ae/common-ui-components-drawer';
import type { CdwStepperComponent } from '@cdw-ae/common-ui-components-stepper';
import type { Job } from '@cdw-ae/job-service';
import { selectJobExecutionByJobId } from '@cdw-ae/jobs-status-state';
import { Store } from '@ngrx/store';
import { filter, Subject, take, takeUntil } from 'rxjs';
import {
  addBulkUpdateAction,
  disableBulkUpdateActionNextbutton,
  enableBulkUpdateActionNextbutton,
  getFieldUpdatesSummary,
  getFieldUpdatesSummarySuccess,
  hideCancellingBulkUpdateAction,
} from './+state/cdw-bulk-update-action.actions';
import type { CdwBulkUpdateActionsState } from './+state/cdw-bulk-update-action.reducer';
import {
  getBulkUpdateActionState,
  selectBulkUpdateActionExists,
} from './+state/cdw-bulk-update-action.selectors';
import type {
  BulkActionEligibilityApi,
  BulkActionEligibilityResult,
  BulkActionSkipGroup,
} from './bulk-action-eligibility-evaluator.interfaces';
import { BULK_ACTION_ELIGIBILITY_API } from './bulk-action-eligibility-evaluator.token';
import type { BulkActionExecutor } from './bulk-action-executor.interfaces';
import { BULK_ACTION_EXECUTOR } from './bulk-action-executor.token';
import {
  type ActionLabels,
  type BulkActionLabelsProvider,
  type ConfirmArchetype,
  DEFAULT_ACTION_LABELS,
} from './bulk-action-labels-provider.interfaces';
import { BULK_ACTION_LABELS_PROVIDER } from './bulk-action-labels-provider.token';
import type { FieldUpdatesSummary } from './models/bulk-update-action-models';


interface AppState {
  cdwDrawersState: CdwDrawersState;
  cdwBulkUpdateActionsState: CdwBulkUpdateActionsState;
}

/**
 * Analytics metadata emitted by bulk action components for GTM tracking.
 * Duplicates the interface from google-tag-manager.service.ts to avoid
 * cross-boundary dependency from library to app.
 */
export interface BulkActionAnalyticsMetadata {
  action_type: string;
  device_count: number;
  fields_updated?: string[];
}

@Component({
  selector: 'lib-cdw-bulk-update-action',
  templateUrl: './cdw-bulk-update-action.component.html',
  styleUrls: ['./cdw-bulk-update-action.component.scss'],
  standalone: false,
})
export class CdwBulkUpdateActionComponent
  implements OnChanges, OnDestroy, OnInit
{
  constructor(
    private appState: Store<AppState>,
    private changeDetector: ChangeDetectorRef,
    private drawerNotifications: DrawerNotificationService,
    private dialog: MatDialog,
    @Optional()
    @Inject(BULK_ACTION_ELIGIBILITY_API)
    private eligibilityApi: BulkActionEligibilityApi | null = null,
    @Inject(BULK_ACTION_EXECUTOR)
    private executor: BulkActionExecutor,
    @Optional()
    @Inject(BULK_ACTION_LABELS_PROVIDER)
    private labelsProvider: BulkActionLabelsProvider | null = null,
  ) {
    this.formatLabel = labelsProvider?.formatLabel?.bind(labelsProvider)
      ?? this.defaultFormatLabel;
  }

  @ViewChildren('fieldUpdate')
  bulkFieldUpdateFields!: QueryList<CdwBulkFieldUpdateComponent>;

  @ViewChild('bulkUpdateStepper') stepper!: CdwStepperComponent;

  /**
   * Emitted immediately when bulk update job is successfully created
   * Parent component should:
   * 1. Create audit log using jobId, updates, and fieldUpdates
   * 2. Call monitorJobAndRefreshGrid(jobId) to monitor job completion
   */
  @Output() bulkActionExecuted = new EventEmitter<{
    jobId: number;
    updates: Record<string, unknown>[];
    fieldUpdates?: Array<{
      field: string;
      updateType: string;
      value: unknown;
    }>;
    analyticsMetadata?: BulkActionAnalyticsMetadata;
  }>();

  @Output() exportSkippedDevices = new EventEmitter<{
    skipGroups: BulkActionSkipGroup<DeviceAsset>[];
    actionType: string;
  }>();

  @Input() fieldUpdateConfigs!: FieldUpdateConfig[];

  @Input() selectedItems: unknown[] = [];

  /**
   * EDUS-2486 Hit-list #8: SSRM consumers don't have a per-row id list
   * — selection lives in Redis on the server. Pass `selectedCount`
   * here to drive ALL drawer display copy ("X Items Selected", warning
   * band counts, summary line, etc.) without relying on
   * `selectedItems.length`. When omitted, the legacy
   * `selectedItems.length` is used (backwards compatible).
   *
   * Note: the eligibility-evaluator legacy fallback at evaluateAndExecute
   * (the `: this.selectedItems` branch) still uses the array; SSRM
   * consumers must wire an evaluator OR rely on Phase 10's
   * BULK_ACTION_EXECUTOR which dispatches via useSelection without
   * needing the per-row array.
   */
  @Input() selectedCount?: number;

  @Input() gridSessionId?: string;

  /**
   * Single source of truth for "how many items are selected" in display
   * copy. Prefers the explicit SSRM `selectedCount` over
   * `selectedItems.length`.
   */
  get effectiveSelectedCount(): number {
    return this.selectedCount ?? this.selectedItems.length;
  }

  /**
   * EDUS-2486 Hit-list #8: detect SSRM mode — the consumer set a
   * non-zero `selectedCount` but `selectedItems` is empty (the per-row
   * id list lives in Redis on the server). In this mode we:
   *  - skip the eligibility evaluator (it can't evaluate without
   *    per-device data; the Summary step's `eligibility.totals.selected`
   *    would otherwise show 0)
   *  - bypass `executeBulkUpdate`'s `targetIds.length === 0` early
   *    return and dispatch with an empty `updates` array + a computed
   *    `updateSpec` so the executor (consumer-side) routes to the
   *    useSelection backend path
   */
  private get isSSrmSelectionMode(): boolean {
    return (
      this.selectedCount !== undefined &&
      this.selectedCount > 0 &&
      this.selectedItems.length === 0
    );
  }

  @Input() drawerHeight = '100%';

  @Input() drawerWidth = '300px';

  @Input() drawerId!: string;

  @Input() bulkUpdateActionId!: string;

  @Input() customerId!: string;

  @Input() drawerTitle = 'Bulk Edit';

  @Input() drawerIcon = 'edit';

  @Input() userEmail!: string;

  @Input() toolbarTitleBackground = '';

  @Input() toolbarTitleColor = '';

  @Input() toolbarTitleFontFamily = '';

  @Input() toolbarTitleFontSize = '';

  @Input() toolbarTitleFontWeight = '';

  @Input() hideHeaderBorder = false;

  /**
   * Optional list of field names to display. If provided, only fields with matching
   * name.value will be shown. If not provided, all fieldUpdateConfigs will be displayed.
   * @example ['orgUnitPath'] - Only show OU field
   * @example ['status'] - Only show device status field
   * @example ['notes', 'annotatedAssetId', 'annotatedUser', 'annotatedLocation'] - Show text fields
   */
  @Input() visibleFields?: string[];

  /**
   * Optional default values to pre-fill when opening the drawer.
   * Array of objects specifying which fields should have default values.
   * @example [{ fieldName: 'status', updateType: UpdateType.REPLACE, value: 'DISABLED' }]
   */
  @Input() defaultValues?: Array<{
    fieldName: string;
    updateType: string;
    value: unknown;
  }>;

  /**
   * The type of action being performed (e.g., 'deprovision', 'move-ou', 'disable', etc.)
   * Used to show action-specific workflow steps
   */
  @Input() actionType?: string;

  /**
   * Optional reminder text shown as a message-band directly above the Next
   * button on the Field Selection and Generic Confirm steps. Empty/undefined
   * hides the band. Consumer decides whether to supply it (e.g. based on
   * actionType). Domain-agnostic string so non-device consumers can vary copy.
   */
  @Input() crossPageReminderText?: string;

  /**
   * Optional device data for validation of Append/Prepend operations
   * Required when editing text fields that support Append/Prepend (e.g., notes)
   */
  @Input() selectedDeviceData: DeviceAsset[] = [];

  private onDestroy$: Subject<void> = new Subject<void>();

  private resetBulkJob$: Subject<void> = new Subject<void>();

  fieldUpdates!: FieldUpdate[] | undefined;

  bulkJob!: Job | undefined;

  isNextButtonDisabled!: boolean;

  isCancellingJob!: boolean;

  fieldUpdateSummaryColumns = ['field', 'type', 'value'];

  fieldUpdateSummary!: FieldUpdatesSummary[];

  // Deprovision-specific properties. The drawer renders Deprovision's
  // 2-step Acknowledge + Reason flow as a special-case until the labels
  // schema grows multi-step Confirm support; until then the host supplies
  // the reason vocabulary via `@Input() deprovisionReasons`.
  deprovisionAcknowledged = false;
  deprovisionReason: string | null = null;

  @Input() deprovisionReasons: Array<{ value: string; label: string }> = [];

  // Generic acknowledge flag for the 'warning-acknowledge' confirm archetype
  // (e.g. Factory Reset). The 'warning' archetype (Profile Wipe) has no
  // checkbox. Deprovision uses its own `deprovisionAcknowledged` flag.
  confirmAcknowledged = false;

  // EDUS-2559: acknowledgment for a blank Replace, which clears existing data
  // on every selected row. Gates the field-selection step's Next button
  // whenever `hasBlankReplace` is true — the Summary step is only reachable
  // through that gate since the stepper has `disableHeaderButtons`, so there
  // is no separate gate on the Summary Execute button. Reset in reset() and
  // whenever the blank-Replace condition clears, so a stale acknowledgment
  // can't carry over to a newly-blanked field.
  blankReplaceAcknowledged = false;

  // Validation state tracking
  fieldValidationStates: Map<string, { valid: boolean; errorMessage?: string; affectedDeviceCount?: number }> = new Map();

  /** Track if user has made any changes (for discard confirmation) */
  hasUnsavedChanges = false;

  /**
   * EDUS-2163: Eligibility result for the current selection.
   * Null when no evaluator is provided (legacy bulk-edit field-update path)
   * OR when the host hasn't supplied an actionType yet.
   */
  eligibility: BulkActionEligibilityResult<DeviceAsset> | null = null;

  /**
   * Status-only and command-only actions both skip the Field Selection step.
   * Status-only actions build the Summary table from `defaultValues`;
   * command-only actions inject a `command` per update row in
   * `executeBulkUpdate`. EDUS-2163 derived from the host-supplied
   * label registration's `kind`, no longer hard-coded to action-type strings.
   */
  get isStatusOnlyAction(): boolean {
    const kind = this.actionLabels.kind;
    return kind === 'status' || kind === 'command';
  }

  /**
   * Command-only actions (e.g. profile-wipe, factory-reset) send a per-row
   * device command instead of status / field overrides and skip the Summary
   * field-update table.
   */
  get isCommandOnlyAction(): boolean {
    return this.actionLabels.kind === 'command';
  }

  /**
   * EDUS-2163 labels for the current actionType. Drives the
   * Summary "pending" line, Completed copy, Progress title, and the derived
   * step indices below. Replaces the per-action `*ngIf` ladders that used to
   * live in the template.
   */
  get actionLabels(): ActionLabels {
    if (this.labelsProvider) {
      return this.labelsProvider.getLabels(this.actionType);
    }
    return DEFAULT_ACTION_LABELS;
  }

  /**
   * Index of the Progress step in the rendered stepper. Derived from the
   * action's step list — eliminates the `actionType === 'deprovision' ? 3 : 2`
   * magic numbers. Progress is always the second-to-last step.
   */
  get progressStepIndex(): number {
    return this.actionLabels.steps.length - 2;
  }

  /**
   * Index of the Completed step. Always the last step in the action's flow.
   */
  get completedStepIndex(): number {
    return this.actionLabels.steps.length - 1;
  }

  /**
   * EDUS-2543 — drawer title with named tokens substituted in
   * (e.g. "Bulk edit - {deviceCount} device(s)" → "Bulk edit - 12 device(s)").
   * Unknown tokens are left intact, so static action titles pass through
   * unchanged. Add an entry to the token map to expose new injected values.
   */
  get drawerTitleLine(): string {
    const tokens: Record<string, string> = {
      deviceCount: String(this.effectiveSelectedCount),
    };
    return this.actionLabels.drawerTitle.replace(
      /\{(\w+)\}/g,
      (match, key) => tokens[key] ?? match,
    );
  }

  /**
   * Format the Summary "pending" line ("This action will update {Y}…") with
   * the current qualified count. Hidden when qualified === 0; the degenerate
   * "no devices eligible" warning band renders instead.
   */
  get summaryPendingLine(): string {
    const qualified = this.eligibility?.totals.qualified ?? this.effectiveSelectedCount;
    return this.formatLabel(this.actionLabels.summaryPendingTemplate, { qualified });
  }

  /**
   * Format the Completed step's success line, substituting the count of
   * actually-acted-on devices reported by the job (falls back to qualified
   * count if the execution row hasn't surfaced yet).
   */
  get completedTitleLine(): string {
    const completedCount =
      this.bulkJob?.completedItems ??
      this.eligibility?.totals.qualified ??
      this.effectiveSelectedCount;
    return this.formatLabel(this.actionLabels.completedTitleTemplate, {
      qualified: completedCount,
    });
  }

  /**
   * Format the Completed step's "X devices were skipped" line. Only rendered
   * when an eligibility evaluator is wired AND skipped > 0.
   */
  get completedSkippedLine(): string {
    const skipped = this.eligibility?.totals.skipped ?? 0;
    return this.formatLabel(this.actionLabels.completedSkippedTemplate, { skipped });
  }

  /**
   * Confirm-step UI shape from the host label registration. Templates render
   * shape-driven blocks instead of `*ngIf="actionType === 'disable' || ..."`
   * ladders.
   */
  get confirmArchetype(): ConfirmArchetype {
    return this.actionLabels.confirmArchetype ?? 'none';
  }

  get confirmStepLabel(): string {
    return this.actionLabels.steps[0] ?? '';
  }

  /** Inner HTML for the warning band ('warning' / 'warning-acknowledge'). */
  get warningBandHtml(): string {
    return this.formatLabel(this.actionLabels.warningBandCopy ?? '', {
      qualified: this.effectiveSelectedCount,
    });
  }

  /** Plain prose under the band (or as the only content for 'prose'). */
  get confirmProse(): string {
    return this.formatLabel(this.actionLabels.confirmProseCopy ?? '', {
      qualified: this.effectiveSelectedCount,
    });
  }

  get acknowledgeLabel(): string {
    return this.actionLabels.acknowledgeLabel ?? '';
  }

  /** Whether the Confirm step requires an acknowledge checkbox. */
  get confirmRequiresAcknowledge(): boolean {
    return this.confirmArchetype === 'warning-acknowledge';
  }

  /**
   * Get filtered field update configs based on visibleFields input
   * If visibleFields is not provided, returns all configs
   */
  get filteredFieldUpdateConfigs(): FieldUpdateConfig[] {
    if (!this.visibleFields || this.visibleFields.length === 0) {
      return this.fieldUpdateConfigs || [];
    }
    const visibleFieldsSet = this.visibleFields;
    return (this.fieldUpdateConfigs || []).filter((config) =>
      visibleFieldsSet.includes(String(config.name.value))
    );
  }

  /**
   * Transform device data to the format needed for validation
   * Extracts notes, annotatedAssetId, annotatedUser, and annotatedLocation from each device
   */
  get deviceMetadataForValidation(): DeviceFieldValues[] {
    return this.selectedDeviceData.map((device) => ({
      deviceId: device.id,
      notes: device.vendorProperties?.google?.notes,
      annotatedAssetId: device.vendorProperties?.google?.annotatedAssetId,
      annotatedUser: device.vendorProperties?.google?.annotatedUser,
      annotatedLocation: device.vendorProperties?.google?.annotatedLocation,
    }));
  }

  /**
   * Check if there are any validation errors across all fields
   */
  get hasValidationErrors(): boolean {
    for (const state of this.fieldValidationStates.values()) {
      if (!state.valid) {
        return true;
      }
    }
    return false;
  }

  /**
   * EDUS-2559: true when at least one visible TEXT or SELECT field uses the
   * Replace operator with an empty value. This is a valid-but-destructive
   * edit — an empty Replace clears the field's existing data on every
   * selected row. It drives the warning band + acknowledge checkbox on the
   * field-selection step, which gates that step's Next button.
   *
   * Excludes TREE fields (e.g. OU) only: `checkFormValidity()` in
   * CdwBulkFieldUpdateComponent already requires a value for
   * `Replace + TREE`, so a tree picker can never reach a blank-Replace state
   * here. TEXT and SELECT both pass that same validation with a blank value,
   * so both must be covered — checking `isTextTemplate` alone missed SELECT
   * fields (e.g. Device Status), letting them clear silently with no warning.
   */
  get hasBlankReplace(): boolean {
    if (!this.bulkFieldUpdateFields) {
      return false;
    }
    return this.bulkFieldUpdateFields.some((field) => {
      const fieldUpdate = (field as unknown as { fieldUpdate?: FieldUpdate })
        .fieldUpdate;
      if (!fieldUpdate || fieldUpdate.updateType !== 'replace') {
        return false;
      }
      if (field.fieldUpdateConfig.template === FieldTemplate.TREE) {
        return false;
      }
      const value = fieldUpdate.value;
      return value === null || value === undefined || String(value).trim() === '';
    });
  }

  public ngOnInit(): void {
    if (!this.bulkUpdateActionId) {
      throw new Error(
        'bulkUpdateActionId not defined on cdw-bulk-update-action'
      );
    }

    this.appState
      .select(selectBulkUpdateActionExists(this.bulkUpdateActionId))
      .pipe(take(1))
      .subscribe((bulkUpdateActionExists) => {
        if (!bulkUpdateActionExists) {
          this.appState.dispatch(
            addBulkUpdateAction({
              id: this.bulkUpdateActionId,
              isNextButtonDisabled: true,
              isCancellingJob: false,
              fieldUpdatesSummary: [],
            })
          );
        }
      });

    this.appState
      .select(getBulkUpdateActionState(this.bulkUpdateActionId))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((state) => {
        ({
          isNextButtonDisabled: this.isNextButtonDisabled,
          isCancellingJob: this.isCancellingJob,
          fieldUpdatesSummary: this.fieldUpdateSummary,
        } = state);
        if (
          this.bulkJob?.status === 'completed' ||
          this.bulkJob?.status === 'error' ||
          this.bulkJob?.status === 'cancelled'
        ) {
          // stepper indices derived from per-action label map
          this.stepper.setStepperStep(this.completedStepIndex);
        }
      });

    // Safety net: reset form state whenever the drawer is programmatically
    // closed (e.g. after job completion). Since EDUS-2593, the header X no
    // longer fires notifyCloseDrawer directly — it now routes through
    // attemptCancel() → reset(), which also lands here. Kept idempotent.
    this.drawerNotifications.closeDrawer$
      .pipe(
        takeUntil(this.onDestroy$),
        filter(({ id }) => id === this.drawerId),
      )
      .subscribe(() => {
        this.resetFormState();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // EDUS-2163: re-evaluate eligibility whenever the host hands us a new
    // selection or refreshed device data. actionType changes also matter
    // because the rules differ per action (e.g. enable vs profile-wipe).
    if (
      // biome-ignore lint/complexity/useLiteralKeys: SimpleChanges uses an index signature
      changes['selectedItems'] ||
      // biome-ignore lint/complexity/useLiteralKeys: SimpleChanges uses an index signature
      changes['selectedDeviceData'] ||
      // biome-ignore lint/complexity/useLiteralKeys: SimpleChanges uses an index signature
      changes['actionType'] ||
      // EDUS-2486 Hit-list #8: SSRM consumers drive the count via this
      // input — re-evaluate when it changes (the legacy selectedItems
      // input stays empty in that mode).
      // biome-ignore lint/complexity/useLiteralKeys: SimpleChanges uses an index signature
      changes['selectedCount']
    ) {
      this.evaluateEligibility();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.resetBulkJob$.next();
  }

  /**
   * EDUS-2163: Run the host-provided eligibility evaluator over the current
   * selection and update Next-button state. No-op (and clears any prior
   * result) when no evaluator is provided or actionType is undefined — that
   * preserves the legacy bulk-edit field-update path, which has its own
   * field-validity gating in onFieldUpdateChange().
   */
  private evaluateEligibility(opts?: Record<string, unknown>): void {
    // Enable the Next button synchronously for status-only / command-only
    // actions so the Confirm step is immediately interactive (the button's
    // own [disabled] binding — e.g. acknowledge checkbox — is the real gate).
    // Without this, the button stays disabled for the full HTTP round-trip.
    if (this.isStatusOnlyAction) {
      this.appState.dispatch(
        enableBulkUpdateActionNextbutton({ id: this.bulkUpdateActionId }),
      );
    }

    if (!(this.eligibilityApi && this.gridSessionId && this.actionType && isEligibilityAction(this.actionType))) {
      this.eligibility = null;
      return;
    }

    this.eligibilityApi
      .evaluate(this.actionType, this.gridSessionId, opts)
      .pipe(take(1), takeUntil(this.onDestroy$))
      .subscribe({
        next: (result) => {
          this.eligibility = result as BulkActionEligibilityResult<DeviceAsset>;
          this.changeDetector.markForCheck();
        },
        error: () => {
          this.eligibility = null;
          this.changeDetector.markForCheck();
        },
      });
  }

  openDrawer() {
    // Reset stepper to first step when opening drawer
    if (this.stepper) {
      this.stepper.reset();
    }

    // Hybrid pattern: dispatch NgRx action AND notify service
    this.appState.dispatch(openDrawer({ id: this.drawerId }));
    this.drawerNotifications.notifyOpenDrawer(this.drawerId);
    // Apply default values after the view has been initialized
    setTimeout(() => {
      this.applyDefaultValues();
      // EDUS-2163: evaluate eligibility once the drawer's initial state is
      // applied so AC 1 / AC 5 (Next-button gating) reflect the selection.
      this.evaluateEligibility();
    }, 0);
  }

  /**
   * Apply default values to field update components
   */
  private applyDefaultValues(): void {
    if (!this.defaultValues || this.defaultValues.length === 0) {
      return;
    }

    if (!this.bulkFieldUpdateFields) {
      return;
    }

    this.defaultValues.forEach((defaultValue) => {
      const fieldComponent = this.bulkFieldUpdateFields.find(
        (field) =>
          String(field.fieldUpdateConfig.name.value) === defaultValue.fieldName
      );

      if (fieldComponent) {
        fieldComponent.setDefaultValues(
          defaultValue.updateType,
          defaultValue.value
        );
      }
    });
  }

  onFieldUpdateChange() {
    if (this.bulkFieldUpdateFields) {
      this.fieldUpdates = this.bulkFieldUpdateFields.map((bulkUpdateField) => {
        const fieldUpdateProperties = bulkUpdateField as unknown as {
          fieldUpdate: FieldUpdate;
        };
        return fieldUpdateProperties.fieldUpdate;
      });

      // Track if user has made any changes (any field is not "keep")
      this.hasUnsavedChanges = this.bulkFieldUpdateFields.some((field) => {
        const fieldUpdateProperties = field as unknown as {
          fieldUpdate: FieldUpdate;
        };
        return fieldUpdateProperties.fieldUpdate.updateType !== 'keep';
      });

      // EDUS-2559: re-require acknowledgment each time the blank-Replace
      // condition clears, so a stale ack can't carry over to a field the user
      // blanks later in the same drawer session.
      if (!this.hasBlankReplace) {
        this.blankReplaceAcknowledged = false;
      }

      this.changeDetector.detectChanges();
      const fieldsValid = this.fieldsValid();
      if (fieldsValid && !this.hasValidationErrors) {
        this.appState.dispatch(
          enableBulkUpdateActionNextbutton({ id: this.bulkUpdateActionId })
        );
      } else {
        this.appState.dispatch(
          disableBulkUpdateActionNextbutton({ id: this.bulkUpdateActionId })
        );
      }
    }
  }

  /**
   * Handle validation state changes from child field update components
   * Records per-field validation state and re-checks overall form validity
   */
  onFieldValidationStateChange(event: ValidationStateChangeEvent): void {
    this.fieldValidationStates.set(event.fieldName, {
      valid: event.valid,
      errorMessage: event.errorMessage,
      affectedDeviceCount: event.affectedDeviceCount,
    });

    // Re-check overall form validity
    this.onFieldUpdateChange();
  }

  /**
   * Generate summary manually from default values for status-only actions
   */
  private generateSummaryFromDefaultValues() {
    if (!this.defaultValues || this.defaultValues.length === 0) {
      return;
    }

    const summary = this.defaultValues.map((defaultValue) => {
      let displayName = defaultValue.fieldName;
      // Map field names to display names
      if (defaultValue.fieldName === 'status') {
        displayName = 'Device Status';
      }

      let updateTypeDisplay = defaultValue.updateType;
      // Map update types to display text
      if (defaultValue.updateType === 'replace') {
        updateTypeDisplay = 'Change to';
      }

      let valueDisplay = String(defaultValue.value);
      // Map values to display text
      if (defaultValue.fieldName === 'status') {
        if (defaultValue.value === 'DISABLED') valueDisplay = 'Disabled';
        else if (defaultValue.value === 'ACTIVE') valueDisplay = 'Active';
        else if (defaultValue.value === 'DEPROVISIONED')
          valueDisplay = 'Deprovisioned';
      }

      return {
        name: displayName,
        updateType: updateTypeDisplay,
        value: valueDisplay,
      };
    });

    this.appState.dispatch(
      getFieldUpdatesSummarySuccess({
        id: this.bulkUpdateActionId,
        summary,
      })
    );
  }

  /**
   * Complete a status-only action step (deprovision reason or disable/enable confirmation)
   * Generates summary from default values and advances to next step
   */
  completeStatusOnlyActionStep() {
    if (this.isStatusOnlyAction) {
      // Generate summary manually from default values since field components don't exist
      this.generateSummaryFromDefaultValues();
    }
    this.stepper.nextStep();
  }

  /**
   * Complete field selection step
   * Step number varies: step 1 for regular actions, step 3 for deprovision
   */
  completeStep1() {
    // Re-evaluate eligibility with current field values so the Summary step
    // reflects field-dependent filtering (e.g. move-ou skips devices already
    // in the target OU).
    const fieldOpts: Record<string, unknown> = {};
    if (this.bulkFieldUpdateFields) {
      this.bulkFieldUpdateFields.forEach((field) => {
        const fu = (field as unknown as { fieldUpdate: FieldUpdate }).fieldUpdate;
        if (fu?.value != null) {
          const fieldName =
            typeof fu.name === 'string' ? fu.name : String(fu.name.value);
          fieldOpts[fieldName] = fu.value;
        }
      });
    }

    const { orgUnitPath: targetOrgUnitPath } = fieldOpts;
    this.evaluateEligibility(
      targetOrgUnitPath != null ? { targetOrgUnitPath } : {},
    );

    this.appState.dispatch(
      getFieldUpdatesSummary({
        id: this.bulkUpdateActionId,
        fieldUpdatesComponents: this.bulkFieldUpdateFields.map((queryList) => ({
          fieldUpdate: queryList.fieldUpdate,
          fieldUpdateConfig: queryList.fieldUpdateConfig,
        })),
      })
    );
    this.stepper.nextStep();
  }

  previousStep() {
    this.stepper.previousStep(true);
  }

  /**
   * EDUS-2486 Hit-list #8: build the per-action update spec without
   * iterating per-device. Mirrors the inner loop body of
   * `executeBulkUpdate`'s `targetIds.map(...)` minus the `deviceId`
   * key. Used in SSRM mode where there are no client-side targetIds —
   * the executor sends `updates: []` + `updateSpec` and the backend
   * (Phase 10's useSelection path) materializes the affected devices
   * from the user's Redis selection set and applies the spec to each.
   *
   * EDUS-2544: append/prepend emit a structured operator
   * `{ op, value }` instead of a resolved string. The client can't resolve
   * them in SSRM mode (no per-device data), so the run-update-batch job step
   * reads each device's current value and concatenates server-side.
   */
  private buildSsrmUpdateSpec(deviceCommand: string | null): Record<string, unknown> {
    const spec: Record<string, unknown> = {};

    if (deviceCommand) {
      // biome-ignore lint/complexity/useLiteralKeys: Record<string, unknown> requires bracket access
      spec['command'] = deviceCommand;
    } else if (
      this.isStatusOnlyAction &&
      this.defaultValues &&
      this.defaultValues.length > 0
    ) {
      this.defaultValues.forEach((defaultValue) => {
        if (defaultValue.updateType !== 'keep') {
          const value = typeof defaultValue.value === 'string'
            ? defaultValue.value.trim()
            : defaultValue.value;
          spec[defaultValue.fieldName] = value;
        }
      });
    } else {
      this.fieldUpdates?.forEach((fieldUpdate) => {
        const fieldName: string =
          typeof fieldUpdate.name === 'string'
            ? fieldUpdate.name
            : String(fieldUpdate.name.value);

        if (fieldUpdate.updateType === 'keep') return;

        const newValue = fieldUpdate.value;
        const isEmptyValue = newValue === undefined || newValue === null || newValue === '';

        if (isEmptyValue) {
          if (fieldUpdate.updateType === 'replace') {
            spec[fieldName] = '';
          }
          return;
        }

        // EDUS-2544: in SSRM mode the client has no per-device data, so it
        // cannot resolve append/prepend itself. Emit a structured operator
        // and let the run-update-batch job step read each device's current
        // value and concatenate server-side. Field-agnostic: any field the
        // bulk-edit config offers append/prepend for flows through here; the
        // backend allow-list (APPENDABLE_FIELDS) is the authority on what's
        // actually supported.
        if (fieldUpdate.updateType === 'append' || fieldUpdate.updateType === 'prepend') {
          spec[fieldName] = {
            op: fieldUpdate.updateType,
            value: typeof newValue === 'string' ? newValue.trim() : String(newValue),
          };
          return;
        }

        spec[fieldName] = typeof newValue === 'string' ? newValue.trim() : newValue;
      });
    }

    if (this.actionType === 'deprovision' && this.deprovisionReason) {
      // biome-ignore lint/complexity/useLiteralKeys: Record<string, unknown> requires bracket access
      spec['deprovisionReason'] = this.deprovisionReason;
    }

    return spec;
  }

  executeBulkUpdate() {
    // SSRM mode ALWAYS dispatches via `useSelection`. Sending
    // `eligibility.qualifiedIds` through the per-row updateDevices path
    // loses the X_CDW_GRID_SESSION header at asset-service-layer's
    // selectionEnabled guard, which drops `gridSessionUuid` from the
    // Firestore execution doc and strands the selection lock. The backend
    // re-evaluates eligibility itself on the useSelection path, so
    // qualifiedIds are unnecessary for dispatch — `eligibility` still
    // drives the Summary / Progress / Completed display copy.
    const hasEligibility = !!this.eligibilityApi;
    const targetIds: readonly unknown[] = this.isSSrmSelectionMode
      ? []
      : hasEligibility
        ? (this.eligibility?.qualifiedIds ?? [])
        : this.selectedItems;

    // In SSRM mode, targetIds is empty by design (above) even when the
    // server has qualified devices — the executor routes via useSelection.
    if (targetIds.length === 0 && !this.isSSrmSelectionMode) return;

    // EDUS-2486 / EDUS-2498: bulkJob progress + complete steps render
    // `{completedItems} of {totalItems}` and "X Devices were
    // successful". The denominator must match what's actually being
    // operated on so the bar reaches 100% at completion. With server-
    // side eligibility filtering, that's `qualified` (drops devices
    // skipped by the evaluator); without an evaluator, fall back to
    // selectedCount which mirrors the server-authoritative selection
    // size and matches the legacy CSRM behavior. This keeps the
    // progress bar consistent with summaryPendingLine and
    // completedTitleLine, which both already use the qualified count.
    const deviceCountForBulkAction =
      this.eligibility?.totals.qualified ?? this.effectiveSelectedCount;

    // EDUS-2544: no client-side append/prepend guard needed — both CSRM and
    // SSRM emit a structured `{ op, value }` operator that the run-update-batch
    // job step resolves server-side (read fresh + etag), so there's no
    // client-side device data to be missing.

    // EDUS-2163 command-only actions (profile-wipe, factory-reset)
    // send `command` per row instead of status / field overrides.
    const deviceCommand = this.actionLabels.command ?? null;

    // Transform field updates into the format expected by asset-service-layer API
    // Each update needs to include the device ID and the fields to update
    const updates = targetIds.map((deviceId) => {
      const update: Record<string, unknown> = { deviceId };

      if (deviceCommand) {
        // biome-ignore lint/complexity/useLiteralKeys: Record<string, unknown> requires bracket access
        update['command'] = deviceCommand;
      } else if (
        this.isStatusOnlyAction &&
        this.defaultValues &&
        this.defaultValues.length > 0
      ) {
        // For status-only actions (disable/enable/deprovision), use defaultValues
        // since fieldUpdates is not populated
        this.defaultValues.forEach((defaultValue) => {
          if (defaultValue.updateType !== 'keep') {
            // Trim string values before saving
            const value = typeof defaultValue.value === 'string'
              ? defaultValue.value.trim()
              : defaultValue.value;
            update[defaultValue.fieldName] = value;
          }
        });
      } else {
        // For regular actions, use fieldUpdates from the field update components
        this.fieldUpdates?.forEach((fieldUpdate) => {
          const fieldName: string =
            typeof fieldUpdate.name === 'string'
              ? fieldUpdate.name
              : String(fieldUpdate.name.value);

          if (fieldUpdate.updateType === 'keep') return;

          const newValue = fieldUpdate.value;
          const isEmptyValue = newValue === undefined || newValue === null || newValue === '';

          if (isEmptyValue) {
            if (fieldUpdate.updateType === 'replace') {
              update[fieldName] = '';
            }
            return;
          }

          if (fieldUpdate.updateType === 'append' || fieldUpdate.updateType === 'prepend') {
            // EDUS-2544: emit a structured operator instead of resolving the
            // concatenation client-side. The run-update-batch job step resolves
            // it against each device's current value (read fresh + If-Match
            // etag), so CSRM and SSRM share one server-side append/prepend
            // implementation — no stale-snapshot reads, no duplicated separator.
            update[fieldName] = {
              op: fieldUpdate.updateType,
              value: typeof newValue === 'string' ? newValue.trim() : String(newValue),
            };
            return;
          }

          update[fieldName] = typeof newValue === 'string' ? newValue.trim() : newValue;
        });
      }

      // Include deprovision reason if this is a deprovision action
      if (this.actionType === 'deprovision' && this.deprovisionReason) {
        // biome-ignore lint/complexity/useLiteralKeys: Record<string, unknown> requires bracket access
        update['deprovisionReason'] = this.deprovisionReason;
      }

      return update;
    });

    const jobType = this.actionLabels.jobType ?? '';

    // Phase 10 of EDUS-2486: also extract a per-action `updateSpec`
    // (single shape applied to all selected rows) alongside the per-row
    // `updates`. Hosts that participate in the server-side selection
    // store can use `{useSelection: true, updateSpec}` to avoid wiring
    // a 30 MB POST body for 300k devices. The executor (consumer-side)
    // decides which mode to use based on whether `updates` is non-empty.
    //
    // updateSpec is the SAME shape as a single `updates[i]` minus
    // `deviceId`. EDUS-2544: for SSRM dispatch (`updates` empty) the spec is
    // rebuilt by `buildSsrmUpdateSpec`, which emits append/prepend as a
    // structured `{ op, value }` operator; the backend resolves it per device.
    // The CSRM `updates[i]` branch now emits the same `{ op, value }` operator
    // too, so both dispatch paths defer append/prepend resolution to the backend.
    //
    // EDUS-2486 Hit-list #8: in SSRM mode `updates` is intentionally
    // empty (no per-row id list client-side), so the updates[0]-based
    // extraction would yield {} and the executor would dispatch a
    // no-op. Rebuild the spec directly from fieldUpdates (or
    // defaultValues for status-only actions, or command for command-
    // only actions) so the useSelection branch of the executor receives
    // a meaningful payload.
    const updateSpec: Record<string, unknown> =
      updates[0]
        ? Object.fromEntries(
            Object.entries(updates[0] as Record<string, unknown>).filter(
              ([key]) => key !== 'deviceId'
            )
          )
        : this.buildSsrmUpdateSpec(deviceCommand);

    // EDUS-2163 executor is mandatory; the drawer no longer carries
    // its own HttpClient / ApiUrlBuilder fallback. Hosts wire backend calls
    // via BULK_ACTION_EXECUTOR (see app.config.ts in the consuming app).
    this.executor
      .execute({
        updates,
        jobType,
        actionType: this.actionType,
        // Pass updateSpec on every dispatch so the executor can choose
        // useSelection mode when appropriate. Always-included signal,
        // always-optional consumption.
        updateSpec,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          const completionStep = this.completedStepIndex;
          if (response?.success) {
            const jobId = response.id;

            // Transform fieldUpdates for audit log
            const fieldUpdates = this.fieldUpdates?.map((fu) => ({
              field:
                typeof fu.name === 'string' ? fu.name : String(fu.name.value),
              updateType: fu.updateType,
              value: fu.value,
            }));

            // Extract fields that were actually sent in the updates payload
            // In CSRM mode: extract from updates[0]
            // In SSRM mode: extract from updateSpec (updates is empty)
            // (includes intentionally-cleared fields, excludes untouched fields)
            // Excludes: deviceId (routing), command (not a field - it's a remote command)
            // Note: updateSpec is always defined (from buildSsrmUpdateSpec), may be empty {}
            const updatedFields = updates.length > 0
              ? Object.keys(updates[0] as Record<string, unknown>).filter(
                  (key) => key !== 'deviceId' && key !== 'command'
                )
              : Object.keys(updateSpec).filter((key) => key !== 'deviceId' && key !== 'command');

            // Emit event immediately to parent component
            // Parent will create audit log, track in GTM/GA4, and monitor job completion
            this.bulkActionExecuted.emit({
              jobId,
              updates,
              fieldUpdates,
              // Analytics metadata for GTM tracking (handled by parent)
              analyticsMetadata: {
                action_type: this.actionType ?? 'unknown',
                device_count: deviceCountForBulkAction,
                ...(updatedFields.length && {
                  fields_updated: updatedFields,
                }),
              },
            });

            // Subscribe to execution updates for internal UI state
            // Jobs are automatically tracked via bindToMyJobs/bindToAllJobs effects
            this.appState
              .select(selectJobExecutionByJobId(jobId))
              .pipe(
                filter((execution) => !!execution),
                takeUntil(this.resetBulkJob$)
              )
              .subscribe((execution) => {
                if (!execution) {
                  return;
                }

                // Map Firestore execution status to bulkJob format for UI display
                const status = execution.executionStatus;

                if (status === ExecutionStatus.finished) {
                  const completedCount =
                    execution.jobCounters?.totalDeviceAssetsWritten ??
                    deviceCountForBulkAction;

                  this.bulkJob = {
                    id: execution.executionId ?? jobId.toString(),
                    status: 'completed',
                    totalItems: deviceCountForBulkAction,
                    completedItems: completedCount,
                  };

                  this.stepper.setStepperStep(completionStep);
                } else if (
                  status === ExecutionStatus.failed ||
                  status === ExecutionStatus.error ||
                  status === ExecutionStatus.cancelledRunning ||
                  status === ExecutionStatus.cancelledPending
                ) {
                  const isCancelled =
                    status === ExecutionStatus.cancelledRunning ||
                    status === ExecutionStatus.cancelledPending;

                  this.bulkJob = {
                    id: execution.executionId || jobId.toString(),
                    status: 'error',
                    totalItems: deviceCountForBulkAction,
                    completedItems: 0,
                    error: isCancelled
                      ? 'Bulk update was cancelled'
                      : (execution.errorMessage ?? 'Bulk update failed'),
                  };

                  this.stepper.setStepperStep(completionStep);
                } else {
                  // Job is still in progress
                  const completedCount =
                    execution.jobCounters?.totalDeviceAssetsWritten ?? 0;

                  this.bulkJob = {
                    id: execution.executionId ?? jobId.toString(),
                    status: 'in-progress',
                    totalItems: deviceCountForBulkAction,
                    completedItems: completedCount,
                  };
                }
              });
          } else {
            // Handle case where API returns success: false
            this.bulkJob = {
              id: 'error',
              status: 'error',
              totalItems: deviceCountForBulkAction,
              completedItems: 0,
              error: 'Bulk update failed',
            };
            this.stepper.setStepperStep(completionStep);
          }
        },
        error: (error) => {
          const errorMessage =
            error?.error?.message ??
            error?.message ??
            'Bulk update request failed';

          this.bulkJob = {
            id: 'error',
            status: 'error',
            totalItems: deviceCountForBulkAction,
            completedItems: 0,
            error: errorMessage,
          };
          this.stepper.setStepperStep(this.completedStepIndex);
        },
      });
    // stepper indices derived from per-action label map
    this.stepper.setStepperStep(this.progressStepIndex);
  }

  /**
   * Attempt to cancel/close the drawer
   * Shows confirmation dialog if there are unsaved changes
   */
  attemptCancel() {
    if (this.hasUnsavedChanges) {
      this.showDiscardChangesDialog();
    } else {
      this.reset();
    }
  }

  /**
   * Show the "Discard unsaved changes?" confirmation dialog
   */
  private showDiscardChangesDialog() {
    const dialogData: CdwConfirmationDialogData = {
      title: 'Unsaved Changes',
      message: '',
      htmlMessage: 'You have unsaved changes to this device. If you continue, those changes will be lost.<br/><br/><strong>Discard unsaved changes?</strong>',
      type: 'warning',
      layout: 'warning-box',
      confirmText: 'Discard Changes',
      cancelText: 'Go Back',
      confirmButtonColor: 'primary',
      showCancelButton: true,
    };

    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '610px',
      data: dialogData,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: { confirmed: boolean }) => {
      if (result?.confirmed) {
        this.reset();
      }
      // If not confirmed, do nothing (stay on the form)
    });
  }

  /**
   * Reset form state without closing the drawer
   * Called when drawer is closed via X button to clear all inputs
   */
  private resetFormState() {
    if (this.bulkFieldUpdateFields) {
      this.bulkFieldUpdateFields.forEach((fieldForm) => {
        fieldForm.resetform();
      });
    }
    this.fieldUpdates = undefined;
    // Reset action-specific properties
    this.deprovisionAcknowledged = false;
    this.deprovisionReason = null;
    this.confirmAcknowledged = false;
    this.blankReplaceAcknowledged = false;
    // Reset validation state
    this.fieldValidationStates.clear();
    // Reset unsaved changes tracking
    this.hasUnsavedChanges = false;
    this.resetBulkJob$.next();
    this.appState.dispatch(
      disableBulkUpdateActionNextbutton({ id: this.bulkUpdateActionId })
    );
    this.appState.dispatch(
      hideCancellingBulkUpdateAction({ id: this.bulkUpdateActionId })
    );
    this.bulkJob = undefined;
    if (this.stepper) {
      this.stepper.reset();
    }
  }

  onExportSkippedDevices(): void {
    if (!(this.eligibility && this.actionType)) return;
    this.exportSkippedDevices.emit({
      skipGroups: this.eligibility.skipGroups as BulkActionSkipGroup<DeviceAsset>[],
      actionType: this.actionType,
    });
  }

  reset() {
    this.resetFormState();
    // Hybrid pattern: dispatch NgRx action AND notify service
    this.appState.dispatch(closeDrawer({ id: this.drawerId }));
    this.drawerNotifications.notifyCloseDrawer(this.drawerId);
  }

  /**
   * Builds failure analytics metadata for GTM tracking.
   * Extracts common logic for both API errors and network errors.
   */

  private fieldsValid() {
    const updateFieldFormsValidities = this.bulkFieldUpdateFields.map(
      (field) => {
        field.checkFormValidity();
        if (field.fieldUpdateForm?.invalid) {
          return false;
        }
        return true;
      }
    );

    if (
      updateFieldFormsValidities.some((isFormValid) => isFormValid === false)
    ) {
      return false;
    }

    // EDUS-2083: Check if at least one field has changes (not "Keep as is")
    // If all fields are set to "keep", the bulk action should not be executable
    const hasAtLeastOneUpdate = this.bulkFieldUpdateFields.some((field) => {
      const fieldUpdateProperties = field as unknown as {
        fieldUpdate: FieldUpdate;
      };
      return fieldUpdateProperties.fieldUpdate.updateType !== 'keep';
    });

    if (!hasAtLeastOneUpdate) {
      return false;
    }

    return true;
  }

  private readonly formatLabel: (
    template: string,
    counts: { qualified?: number; skipped?: number },
  ) => string;

  private defaultFormatLabel(
    template: string,
    counts: { qualified?: number; skipped?: number },
  ): string {
    let out = template;
    if (counts.qualified !== undefined) {
      out = out.replace(/\{Y\}/g, String(counts.qualified));
    }
    if (counts.skipped !== undefined) {
      out = out.replace(/\{Z\}/g, String(counts.skipped));
    }
    return out;
  }
}
